import { CircularProgress } from '@material-ui/core';
import { AxiosError } from 'axios';
import { Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import React, { useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import baseForm from 'assets/baseForm';
import reducer from 'app/pages/Registration/reducer';
import ErplySignupService from 'service/ErplySignupService';
import { StyledStep } from 'app/components/StyledStep';
import { AbsoluteCenter } from 'app/components/styles';
import steps from 'app/pages/Registration/steps';

import { generateInitialForm, IForm } from './utils';

const Registration: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [{ activeStep }, dispatch] = useReducer(reducer, {
    activeStep: 0,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const memoSteps = useMemo(() => steps(baseForm, t), [t]);
  const formInitialValues = useMemo(() => generateInitialForm(baseForm), []);
  const handleStep = async ({
    validateForm,
    i,
  }: {
    validateForm: FormikHelpers<IForm>['validateForm'];
    i: number;
  }) => {
    const errors = await validateForm();
    dispatch({ i, errors });
  };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={memoSteps[activeStep].validation}
      onSubmit={async (data, { validateForm, setErrors }) => {
        if (activeStep === memoSteps.length - 1) {
          if (!isSubmitted) {
            try {
              setIsSubmitted(true);
              const userData = await ErplySignupService.createAccount(data);
              history.push('/final', userData);
            } catch (e) {
              const error = e as AxiosError<{ message: string }>;
              if (error.isAxiosError) {
                setErrors({
                  confirm: {
                    confirm:
                      error.response?.data.message ??
                      t('registration.confirm.submitError'),
                  },
                });
              } else {
                setErrors({
                  confirm: { confirm: t('registration.confirm.submitError') },
                });
              }
            }
          }
        } else {
          handleStep({
            validateForm,
            i: activeStep + 1,
          }).then();
        }
      }}
    >
      {({ handleSubmit, validateForm, errors, values }) => (
        <StyledStep.LayoutForm onSubmit={handleSubmit}>
          {isSubmitted && (
            <AbsoluteCenter>
              <CircularProgress />
            </AbsoluteCenter>
          )}
          {memoSteps.map(
            ({ validation, content, path, info }, i) =>
              i === activeStep && (
                <React.Fragment key={path}>
                  {i > 0 && (
                    <StyledStep.PrevStep
                      handlePrevStep={async () => {
                        await handleStep({
                          validateForm,
                          i: i - 1,
                        });
                      }}
                    />
                  )}
                  <StyledStep.Content $usePadding={i === 0}>
                    <StyledStep.Title
                      className="form-field__max-width"
                      variant="h6"
                      align="center"
                    >
                      {info}
                    </StyledStep.Title>
                    {content}
                    <StyledStep.Fade>
                      <StyledStep.NextStep
                        values={values}
                        index={i}
                        errors={errors[path]}
                        validation={validation}
                        isSubmit={activeStep === memoSteps.length - 1}
                        isSubmitted={isSubmitted}
                        handleNextStep={async () => {
                          await handleStep({
                            validateForm,
                            i: i + 1,
                          });
                        }}
                      />
                    </StyledStep.Fade>
                  </StyledStep.Content>
                </React.Fragment>
              ),
          )}
        </StyledStep.LayoutForm>
      )}
    </Formik>
  );
};

export default Registration;
